import { useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import type { Application } from '../types/application';
import {
  getInflowBucket,
  getInflowLabel,
  getInflowColor,
  INFLOW_BUCKET_UNKNOWN,
  INFLOW_BUCKET_MANUAL,
} from '../../../constants/inflowChannels';

/** 채널별 집계 행 */
export interface InflowChannelRow {
  bucket: string;
  label: string;
  color: string;
  count: number;
  sharePct: number;       // 전체 대비 비중 (%)
  completed: number;      // status === '완료'
  inProgress: number;     // 완료/반송 외 (OrdersPage '진행중' 정의와 동일)
  returned: number;       // status === '반송'
  totalPriceOffer: number; // 검수금액 합계
}

export interface InflowStats {
  total: number;
  trackedCount: number;       // inflow 수집된 건수
  manualCount: number;        // 수동등록 버킷 건수
  trackingRate: number;       // 추적률 % (수동등록 제외 분모)
  topChannel: InflowChannelRow | null;   // 미수집/수동등록 제외 최다 채널
  activeChannelCount: number;            // 미수집/수동등록 제외 활성 채널 수
  share: { name: string; value: number; color: string }[];        // 도넛용 (건수 desc)
  daily: Record<string, number | string>[];                        // 일별 스택 데이터
  dailySeries: { bucket: string; label: string; color: string }[]; // 스택 시리즈 정의
  table: InflowChannelRow[];                                       // 채널별 표 (건수 desc)
}

/** 일별 스택 차트 시리즈 상한 (초과분은 '기타'로 합산해 가독성 유지) */
const MAX_DAILY_SERIES = 8;
const ETC_SERIES_KEY = '__etc__';

/**
 * 유입경로 통계 집계 훅
 * @param applications 기간 필터가 이미 적용된 신청 목록
 * @param chartStart 일별 차트 X축 시작일
 * @param chartEnd 일별 차트 X축 종료일
 */
export const useInflowStats = (
  applications: Application[],
  chartStart: Dayjs,
  chartEnd: Dayjs,
): InflowStats => {
  const chartStartMs = chartStart.valueOf();
  const chartEndMs = chartEnd.valueOf();

  return useMemo(() => {
    const rows = new Map<string, InflowChannelRow>();
    const ensureRow = (bucket: string): InflowChannelRow => {
      let row = rows.get(bucket);
      if (!row) {
        row = {
          bucket,
          label: getInflowLabel(bucket),
          color: getInflowColor(bucket),
          count: 0,
          sharePct: 0,
          completed: 0,
          inProgress: 0,
          returned: 0,
          totalPriceOffer: 0,
        };
        rows.set(bucket, row);
      }
      return row;
    };

    // 일별 맵 pre-seed (빈 날짜도 0으로 채워 X축 공백 방지)
    const dayKeys: string[] = [];
    const dailyMap = new Map<string, Record<string, number | string>>();
    let cursor = dayjs(chartStartMs).startOf('day');
    const endDay = dayjs(chartEndMs).startOf('day');
    while (cursor.isBefore(endDay) || cursor.isSame(endDay)) {
      const key = cursor.format('YYYY-MM-DD');
      dayKeys.push(key);
      dailyMap.set(key, { dateKey: key, label: cursor.format('MM/DD') });
      cursor = cursor.add(1, 'day');
    }

    const total = applications.length;
    let trackedCount = 0;
    let manualCount = 0;

    applications.forEach((app) => {
      const bucket = getInflowBucket(app);
      if (app.inflow?.source) trackedCount++;
      if (bucket === INFLOW_BUCKET_MANUAL) manualCount++;

      const row = ensureRow(bucket);
      row.count++;
      if (app.status === '완료') row.completed++;
      else if (app.status === '반송') row.returned++;
      else row.inProgress++;
      row.totalPriceOffer += Number(app.total_price_offer ?? app.price_offer ?? 0) || 0;

      const createdSec = app.created_at?.seconds;
      if (createdSec) {
        const dayKey = dayjs.unix(createdSec).format('YYYY-MM-DD');
        const day = dailyMap.get(dayKey);
        if (day) {
          day[bucket] = ((day[bucket] as number) || 0) + 1;
        }
      }
    });

    const table = [...rows.values()]
      .map((r) => ({
        ...r,
        sharePct: total > 0 ? Math.round((r.count / total) * 1000) / 10 : 0,
      }))
      .sort((a, b) => b.count - a.count);

    const realChannels = table.filter(
      (r) => r.bucket !== INFLOW_BUCKET_UNKNOWN && r.bucket !== INFLOW_BUCKET_MANUAL,
    );
    const topChannel = realChannels[0] ?? null;
    const activeChannelCount = realChannels.length;

    // 추적률: 수동등록(수집 대상 아님)을 분모에서 제외
    const trackableTotal = Math.max(total - manualCount, 0);
    const trackingRate = trackableTotal > 0
      ? Math.round((trackedCount / trackableTotal) * 100)
      : 0;

    const share = table.map((r) => ({ name: r.label, value: r.count, color: r.color }));

    // 일별 스택 시리즈: 건수 상위 N개 + 나머지 '기타' 합산
    const topBuckets = table.slice(0, MAX_DAILY_SERIES).map((r) => r.bucket);
    const overflowBuckets = table.slice(MAX_DAILY_SERIES).map((r) => r.bucket);
    const dailySeries = [
      ...table.slice(0, MAX_DAILY_SERIES).map((r) => ({
        bucket: r.bucket,
        label: r.label,
        color: r.color,
      })),
      ...(overflowBuckets.length > 0
        ? [{ bucket: ETC_SERIES_KEY, label: '기타', color: '#94a3b8' }]
        : []),
    ];

    const daily = dayKeys.map((key) => {
      const day = dailyMap.get(key)!;
      if (overflowBuckets.length > 0) {
        let etc = 0;
        overflowBuckets.forEach((b) => {
          etc += (day[b] as number) || 0;
        });
        day[ETC_SERIES_KEY] = etc;
      }
      topBuckets.forEach((b) => {
        if (day[b] === undefined) day[b] = 0;
      });
      return day;
    });

    return {
      total,
      trackedCount,
      manualCount,
      trackingRate,
      topChannel,
      activeChannelCount,
      share,
      daily,
      dailySeries,
      table,
    };
  }, [applications, chartStartMs, chartEndMs]);
};
